import { ArrowLeft, Download, FileUp } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import type { CourseDocument } from '../../lib/courses.ts';
import { importCourse, lireDocument } from '../../lib/courses.ts';

/* Importer un parcours exporté ailleurs.
 *
 * ── Le document fait autorité ───────────────────────────────────────────────
 *
 * L'import ÉCRASE le golf du même nom dans la même ville, ou le crée s'il n'y
 * est pas. C'est ce qui rend le cycle utile : exporter, corriger le fichier
 * dans un éditeur de texte, réimporter — sans avoir à supprimer quoi que ce
 * soit entre les deux.
 *
 * Ce qui est écrasé n'est que la fiche et le carnet. Les parties déjà jouées
 * pendent des identifiants de trous et de départs, préservés à l'écriture :
 * réimporter un parcours ne fait disparaître le score de personne.
 *
 * ── Fichier et collage ──────────────────────────────────────────────────────
 *
 * Le fichier est le chemin principal, parce que c'est lui qu'on vient de
 * corriger. Le collage reste offert : entre deux instances ouvertes côte à
 * côte, il évite l'aller-retour par le disque.
 */

export function CourseImport() {
    const navigate = useNavigate();
    const [texte, setTexte] = useState('');
    const [nomFichier, setNomFichier] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fichier = useRef<HTMLInputElement>(null);

    const lireFichier = (f: File): void => {
        setError(null);
        void f.text()
            .then((t) => { setTexte(t); setNomFichier(f.name); })
            .catch(() => setError('Fichier illisible.'));
    };

    const envoyer = async (): Promise<void> => {
        setBusy(true);
        setError(null);
        let doc: CourseDocument;
        try {
            doc = lireDocument(texte);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Document illisible.');
            setBusy(false);
            return;
        }
        try {
            const { id } = await importCourse(doc);
            // Droit au carnet : c'est ce qu'on vient chercher, et c'est là
            // qu'on verra tout de suite si l'import est complet.
            navigate(`/parcours/${id}/carnet`);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Import impossible.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4 py-2">
            <header>
                <Link to="/parcours" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Parcours
                </Link>
                <h1 className="text-2xl font-bold tracking-tight">Importer un parcours</h1>
                {/* Une phrase, parce que la conséquence n'est pas devinable
                    depuis un bouton : ce geste peut écraser un parcours. */}
                <p className="mt-1 text-sm opacity-70">
                    Le fichier remplace le golf du même nom, ou le crée s'il n'existe pas.
                </p>
            </header>

            <input
                ref={fichier}
                type="file"
                accept="application/json,.json,text/plain,.txt"
                className="hidden"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) lireFichier(f);
                }}
            />
            <button type="button" onClick={() => fichier.current?.click()}
                className="btn btn-neutral btn-block h-14 gap-2 text-base font-bold tap-target">
                <FileUp size={20} /> Choisir un fichier
            </button>

            <section className="rounded-box bg-base-200 p-4">
                <label htmlFor="doc" className="text-sm font-semibold">
                    {nomFichier ? `Contenu de ${nomFichier}` : 'Ou collez le document'}
                </label>
                <textarea
                    id="doc"
                    rows={8}
                    spellCheck={false}
                    className="textarea textarea-bordered mt-2 w-full font-mono text-xs"
                    placeholder={'{ "format": "golf.parcours/1", … }'}
                    value={texte}
                    onChange={(e) => { setTexte(e.target.value); setNomFichier(null); }}
                />
            </section>

            {error && (
                <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>
            )}

            <button type="button" disabled={busy || !texte.trim()}
                onClick={() => void envoyer()}
                className="btn btn-primary btn-block h-14 gap-2 text-lg font-bold tap-target">
                <Download size={20} /> Importer
            </button>
        </div>
    );
}
